import { atom as w, map as b, computed as M } from "nanostores";
import { copilotTools as q, copilotInstructions as D, copilotContext as L, uniqueId as E } from "./global-state.js";
function v() {
  return Math.floor(Date.now() / 1e3);
}
const F = {
  idle: ["initializing", "ending"],
  initializing: ["ready", "error"],
  ready: ["sending", "ending"],
  sending: ["receiving"],
  receiving: ["ready", "reconnecting"],
  reconnecting: ["ready", "ending"],
  ending: ["ended", "error"],
  error: ["initializing", "ending"],
  ended: []
  // Can only transition to itself (terminal state)
}, k = "The previous tool call did not complete. The action may not have run. Tell the user the action did not complete and offer to try again — do not claim success or failure.";
function z(r, e) {
  if (r === e)
    return r;
  if (!F[r])
    throw new Error(`Unknown status: ${r}`);
  if (F[r].includes(e))
    return e;
  throw new Error(`Invalid status transition: ${r} -> ${e}`);
}
class X {
  constructor(e) {
    this.__id = w(void 0), this.config = {
      includeGlobalContext: !0,
      includeGlobalTools: !0,
      includeGlobalInstructions: !0
    }, this.__status = w("idle"), this.__messages = w([]), this.__meta = w({}), this.__uploadFiles = w([]), this.__queuedMessages = w([]), this.__orphanedToolCallIds = w(/* @__PURE__ */ new Set()), this.__abortController = null, this.__enableWebSearch = w(!0), this.__serverSideTools = b({}), this.__sessionInstructions = b({}), this.__sessionTools = b({}), this.__sessionContext = b({}), this.__tools = M(
      [this.__serverSideTools, q, this.__sessionTools],
      (s, i, n) => {
        const o = {}, h = {
          ...this.config.includeGlobalTools === !0 ? i : {},
          ...n
        };
        for (const p of Object.values(s))
          if (p != null) {
            const g = h[p.name];
            o[p.name] = {
              ...g?.type === "server" ? g : void 0,
              ...p
            };
          }
        for (const p of Object.values(h))
          p != null && p.type === "client" && o[p.name] == null && (o[p.name] = p);
        return o;
      }
    ), this.__statefulMessages = M(
      [this.__messages, this.__tools, this.__orphanedToolCallIds],
      (s, i, n) => $(
        s,
        i,
        n,
        (o, a, h) => this.respondToToolCall(o, a, h)
      )
    ), this.__instructions = M(
      [D, this.__sessionInstructions],
      (s, i) => ({
        ...this.config.includeGlobalInstructions === !0 ? s : {},
        ...i
      })
    ), this.__context = M([L, this.__sessionContext], (s, i) => ({
      ...this.config.includeGlobalContext === !0 ? s : {},
      ...i
    })), this.__respondedToolCallIds = /* @__PURE__ */ new Set(), this.__suggestionsRefreshTrigger = w([]), e && (this.config = { ...this.config, ...e });
  }
  get id() {
    return this.__id.get();
  }
  set id(e) {
    this.__id.set(e != null ? String(e) : void 0);
  }
  /**
   * The current status of the Copilot session.
   */
  get status() {
    return this.__status.get();
  }
  /**
   * If the session is being ended.
   */
  get isEnding() {
    return this.status === "ending";
  }
  get isEnded() {
    return this.status === "ended";
  }
  /**
   * Sets the status of the Copilot session.
   */
  setStatus(e) {
    this.__status.set(z(this.status, e));
  }
  /**
   * adds an instruction just for this session
   */
  addInstruction(e) {
    const s = E();
    return this.__sessionInstructions.setKey(s, e), () => {
      this.__sessionInstructions.setKey(s, void 0);
    };
  }
  /**
   * Adds a tool just for this session
   */
  addTool(e) {
    return this.__sessionTools.setKey(e.name, e), () => {
      this.__sessionTools.setKey(e.name, void 0);
    };
  }
  /**
   * Adds a context just for this session
   */
  addContext(e) {
    const s = E();
    return this.__sessionContext.setKey(s, e), () => {
      this.__sessionContext.setKey(s, void 0);
    };
  }
  /**
   * Sets the meta data for this session
   */
  setSessionMeta(e) {
    this.__meta.set(e);
  }
  /**
   * Gets the meta data for this session
   */
  getSessionMeta() {
    return this.__meta.get();
  }
  /**
   * Sets whether the model can search the web to answer questions.
   */
  setWebSearchEnabled(e) {
    this.__enableWebSearch.set(e);
  }
  /**
   * Gets whether web search is enabled for this session.
   */
  getWebSearchEnabled() {
    return this.__enableWebSearch.get();
  }
  /**
   * Updates the meta data for a specific message by targeting message.meta.id
   */
  setMessageMeta(e, s) {
    const n = this.__messages.get().map((o) => o.role === "model" && o.type === "meta" && "meta" in o && o.meta?.id === e ? {
      ...o,
      meta: s
    } : o);
    this.__messages.set(n);
  }
  /**
   * Sends a tool_response for a human-in-the-loop tool call.
   *
   * Idempotent — calling this more than once for the same tool call ID
   * is a no-op. This protects against double-clicks and React re-renders
   * that could otherwise send duplicate responses.
   */
  respondToToolCall(e, s, i) {
    this.__respondedToolCallIds.has(e) || (this.__respondedToolCallIds.add(e), this.sendMessage([
      {
        role: "user",
        type: "tool_response",
        tool_responses: [
          {
            tool_call_id: e,
            name: s,
            response: { result: i, error: null },
            finished: !0
          }
        ],
        timestamp: v()
      }
    ]).catch(() => {
      this.__respondedToolCallIds.delete(e);
    }));
  }
  /**
   * Clears any messages that are queued to be sent after the current response completes.
   * Returns the messages that were cleared so they can be restored if needed.
   */
  clearQueuedMessages() {
    const e = this.__queuedMessages.get();
    return this.__queuedMessages.set([]), e;
  }
  /**
   * Stops the current in-flight exchange.
   *
   * Aborts the streaming HTTP request immediately (so the UI becomes responsive
   * right away) and fires a request to cancel the Temporal workflow on the
   * backend (so it stops burning LLM tokens).
   *
   * Always returns `{ messages }` — the messages that were queued while the
   * exchange was running — so the caller can restore them to the input box
   * regardless of whether the backend call succeeded. If the backend call
   * fails, `error` is also set so the caller can surface it to the user.
   *
   * Never throws. Safe to call in any state.
   */
  async stop() {
    if (this.status !== "sending" && this.status !== "receiving" && this.status !== "reconnecting")
      return { messages: [] };
    if (this.status === "reconnecting") {
      const i = this.clearQueuedMessages();
      this.setStatus("ready");
      const n = window.__hydra_copilot__?.stopExchange;
      if (n != null && this.id != null)
        try {
          await n({ id: this.id });
        } catch (o) {
          return {
            messages: i,
            error: o instanceof Error ? o : new Error(String(o))
          };
        }
      return { messages: i };
    }
    this.__abortController?.abort();
    const e = this.clearQueuedMessages(), s = window.__hydra_copilot__?.stopExchange;
    if (s != null && this.id != null)
      try {
        await s({ id: this.id });
      } catch (i) {
        return {
          messages: e,
          error: i instanceof Error ? i : new Error(String(i))
        };
      }
    return { messages: e };
  }
  /**
   * Forces a refresh of suggestions for this session.
   * This will trigger any active suggestion hooks to regenerate their suggestions.
   * @param instruction - Custom instructions to include in the next suggestions fetch.
   * These instructions will be used once and then cleared.
   */
  refreshSuggestions(e) {
    this.__suggestionsRefreshTrigger.set([{ instruction: e }]);
  }
  /**
   * Initializes a new session, either from an existing session id,
   * or by creating a new one
   */
  async initialize(e) {
    if (this.status !== "idle" && this.status !== "error")
      return this.id;
    this.setStatus("initializing");
    try {
      const s = window.__hydra_copilot__?.startSession;
      if (s == null)
        throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
      if (e == null) {
        const i = await s();
        if (i.id == null)
          throw new Error("Failed to start session, no session ID returned");
        this.id = i.id, this.__serverSideTools.set(R(i.tools ?? []));
      } else {
        this.id = e;
        const i = await this.loadSession();
        this.__messages.set(i.messages ?? []), this.__meta.set(i.meta ?? {}), this.__serverSideTools.set(R(i.tools ?? [])), this.__orphanedToolCallIds.set(/* @__PURE__ */ new Set());
      }
      return this.setStatus("ready"), this.id;
    } catch (s) {
      throw this.setStatus("error"), s;
    }
  }
  /**
   * Ends the current session, clearing all messages and state.
   */
  async end() {
    this.setStatus("ending");
    try {
      const e = window.__hydra_copilot__?.endSession;
      if (e == null)
        throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
      this.id != null && await e(this.id), this.__messages.set([]), this.__queuedMessages.set([]), this.__respondedToolCallIds.clear(), this.__orphanedToolCallIds.set(/* @__PURE__ */ new Set()), this.id = void 0, this.__sessionInstructions.set({}), this.__sessionTools.set({}), this.__sessionContext.set({}), this.__serverSideTools.set({}), this.__meta.set({}), this.setStatus("ended");
    } catch (e) {
      throw this.setStatus("error"), e;
    }
  }
  /**
   * Marks any non-approval tool calls without responses as orphaned. Called at every point
   * where the active stream ends (recovery exhausted, recovery fail, error/abort path),
   * so the UI stops showing them as spinning. Pure UI flag — doesn't write to __messages.
   */
  markOrphanedToolCalls() {
    const e = this.__messages.get(), s = this.__tools.get(), i = /* @__PURE__ */ new Set();
    for (const a of e)
      if (a.role === "user" && a.type === "tool_response")
        for (const h of a.tool_responses)
          i.add(h.tool_call_id);
    const n = new Set(this.__orphanedToolCallIds.get());
    let o = !1;
    for (const a of e)
      if (!(a.role !== "model" || a.type !== "tool_call"))
        for (const h of a.tool_calls)
          i.has(h.id) || h.needs_approval === !0 || s[h.name]?.humanInTheLoop === !0 || n.has(h.id) || (n.add(h.id), o = !0);
    o && this.__orphanedToolCallIds.set(n);
  }
  /**
   * Returns pending approval tool calls from the session's messages.
   * A pending approval is a tool call with `needs_approval === true` that
   * doesn't yet have a corresponding tool_response message.
   */
  getPendingApprovals() {
    const e = this.__messages.get(), s = /* @__PURE__ */ new Set();
    for (const n of e)
      if (n.role === "user" && n.type === "tool_response")
        for (const o of n.tool_responses)
          s.add(o.tool_call_id);
    const i = [];
    for (const n of e)
      if (n.role === "model" && n.type === "tool_call")
        for (const o of n.tool_calls)
          o.needs_approval === !0 && !s.has(o.id) && i.push({ id: o.id, name: o.name });
    return i;
  }
  async sendMessage(e) {
    let s = typeof e == "string" ? [{ role: "user", type: "text", text: e, timestamp: v() }] : [...e];
    const i = window.__hydra_copilot__?.sendStreamingMessage;
    if (i == null)
      throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
    if ((this.status === "idle" || this.status === "error") && await this.initialize(), this.status === "initializing" && await this.waitUntilReady(), this.status !== "ready") {
      if (this.status === "sending" || this.status === "receiving" || this.status === "reconnecting") {
        this.__queuedMessages.set([...this.__queuedMessages.get(), ...s]);
        return;
      }
      throw new Error(`Cannot send message while session is in "${this.status}" state.`);
    }
    if (!s.some((p) => p.type === "tool_response")) {
      const p = this.getPendingApprovals();
      p.length > 0 && (s = [B(p), ...s]);
    }
    let o = null, a = 0, h = !1;
    try {
      this.setStatus("sending"), this.__abortController = new AbortController();
      let p = s.map((g) => ({
        ...g,
        timestamp: g.timestamp || v()
      }));
      for (; p.length > 0; ) {
        this.__messages.set([...this.__messages.get(), ...p]);
        const g = [], m = (t) => {
          this.setStatus("receiving"), a += 1, t.type === "meta" && (h = !0);
          let u = t;
          if (t.role === "model" && t.type === "tool_call") {
            const d = t.tool_calls.map((l) => {
              if (l.is_client_tool === !0 && !l.needs_approval) {
                const c = this.__tools.get()[l.name];
                if (U(c?.needsApproval, l.args))
                  return { ...l, needs_approval: !0 };
              }
              return l;
            });
            u = { ...t, tool_calls: d };
          }
          if (this.__messages.set([...this.__messages.get(), u]), u.role === "model" && u.type === "tool_call")
            for (const d of u.tool_calls) {
              if (d.needs_approval || d.is_client_tool !== !0)
                continue;
              const l = this.__tools.get()[d.name];
              l?.humanInTheLoop !== !0 && g.push(
                (async () => {
                  try {
                    if (l?.type === "server")
                      throw new Error(
                        `Tool ${d.name} is a server-side tool and cannot be called on the client.`
                      );
                    if (l?.handler == null)
                      throw new Error(`Tool ${d.name} does not have a handler defined.`);
                    const c = await l.handler({ args: d.args, session: this, toolCallId: d.id }) ?? null;
                    return {
                      tool_call_id: d.id,
                      name: d.name,
                      response: { result: c, error: null },
                      finished: !0
                    };
                  } catch (c) {
                    return {
                      tool_call_id: d.id,
                      name: d.name,
                      response: {
                        result: null,
                        error: `Error calling tool ${d.name}: ${c instanceof Error ? c.message : String(c)}`
                      },
                      finished: !0
                    };
                  }
                })()
              );
            }
          if (t.role === "user" && t.type === "tool_response")
            for (const d of t.tool_responses) {
              const l = this.__tools.get()[d.name], c = Q(
                [...this.__messages.get(), ...p],
                d.tool_call_id
              );
              l?.type === "server" && l?.handler != null && d.finished === !0 && l.handler({
                args: c?.args || {},
                session: this,
                response: d.response,
                toolCallId: d.tool_call_id,
                wasApproved: d.was_approved === !0
              });
            }
        }, y = {
          id: this.id,
          messages: p,
          tools: W(this.__tools.get()),
          instructions: G(this.__instructions.get()),
          context: K(this.__context.get()),
          onMessage: m,
          signal: this.__abortController?.signal,
          enable_web_search: this.getWebSearchEnabled()
        };
        if (await i(y), g.length === 0) {
          p = [];
          continue;
        }
        p = [
          {
            role: "user",
            type: "tool_response",
            tool_responses: await Promise.all(g),
            timestamp: v()
          }
        ];
      }
      if (a === 0)
        throw new Error("No messages received from the server. Please check your connection.");
      if (a > 0 && !h) {
        this.setStatus("reconnecting"), this.recoverInterruptedStream().catch(() => {
          this.status === "reconnecting" && (this.markOrphanedToolCalls(), this.setStatus("ready"));
        });
        return;
      }
      this.markOrphanedToolCalls(), this.setStatus("ready"), o = O(this.__queuedMessages);
    } catch (p) {
      if (this.markOrphanedToolCalls(), this.setStatus("receiving"), this.setStatus("ready"), J(p))
        return;
      throw p;
    } finally {
      this.__abortController = null;
    }
    o != null && o.length > 0 && this.sendMessage(o).catch(() => {
    });
  }
  /**
   * Attempts to recover from an interrupted stream by reloading the session
   * from the backend. The backend continues the LLM call without streaming
   * when the connection drops, so the full response is eventually persisted.
   *
   * Retries with increasing delays to give the workflow time to complete.
   * On success, replaces local messages with the server state and transitions
   * to 'ready'. On failure, appends an error message and transitions to 'ready'.
   */
  async recoverInterruptedStream() {
    if (this.id == null || window.__hydra_copilot__?.getSession == null) {
      this.markOrphanedToolCalls(), this.setStatus("ready");
      return;
    }
    const e = [2e3, 3e3, 5e3, 5e3, 1e4];
    for (const s of e) {
      if (await new Promise((o) => setTimeout(o, s)), this.status !== "reconnecting")
        return;
      let i;
      try {
        const o = await this.loadSession();
        i = { messages: o.messages ?? [], meta: o.meta ?? {} };
      } catch {
        continue;
      }
      if (i.messages[i.messages.length - 1]?.type === "meta") {
        if (this.status !== "reconnecting")
          return;
        this.__messages.set(i.messages), this.__meta.set(i.meta), this.__orphanedToolCallIds.set(/* @__PURE__ */ new Set()), this.setStatus("ready"), this.drainAndSendQueuedMessages();
        return;
      }
    }
    this.status === "reconnecting" && (this.__messages.set([
      ...this.__messages.get(),
      {
        role: "model",
        type: "error",
        error: "The connection was interrupted. Please try sending your message again.",
        timestamp: v()
      }
    ]), this.markOrphanedToolCalls(), this.setStatus("ready"));
  }
  /**
   * Drains the message queue and sends the combined messages.
   * Combines text messages into a single message to avoid flooding the LLM.
   */
  drainAndSendQueuedMessages() {
    const e = O(this.__queuedMessages);
    e.length !== 0 && this.sendMessage(e).catch(() => {
    });
  }
  /**
   * Loads the session data (messages and meta)
   */
  async loadSession() {
    const e = window.__hydra_copilot__?.getSession;
    if (e == null)
      throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
    if (this.id == null)
      throw new Error("Session not started");
    return await e(this.id);
  }
  /**
   * Waits for the session to be ready
   */
  async waitUntilReady() {
    if (this.status !== "ready") {
      if (this.status === "idle") {
        await this.initialize();
        return;
      }
      if (this.status === "error")
        throw new Error("Session is in error state");
      if (this.status === "ending" || this.status === "ended")
        throw new Error(`Cannot wait for session in "${this.status}" state`);
      return new Promise((e, s) => {
        let i = null, n = null;
        const o = () => {
          i !== null && (clearTimeout(i), i = null), n !== null && (n(), n = null);
        };
        n = this.__status.subscribe((a) => {
          a === "ready" ? (o(), e()) : a === "error" ? (o(), s(new Error("Session failed while waiting for ready"))) : (a === "ending" || a === "ended") && (o(), s(new Error("Session ended while waiting for ready")));
        }), i = setTimeout(() => {
          o(), s(new Error("Waiting for session to be ready timed out - exceeded 30 seconds"));
        }, 3e4);
      });
    }
  }
  /**
   * Gets upload signature for file upload
   * Note: Session readiness is now checked at the upload level (uploadCopilotFile)
   */
  async getUploadSignature(e) {
    if (!this.id)
      throw new Error("Session ID not available - ensure session is ready before calling getUploadSignature");
    const s = window.__hydra_copilot__;
    if (!s?.getUploadSignature)
      throw new Error("File upload not configured. Please provide getUploadSignature in setCopilotConfig.");
    return await s.getUploadSignature({
      id: this.id,
      size: e.size,
      mime_type: e.mime_type
    });
  }
  /**
   * Gets download URL for a previously uploaded file
   */
  async getFileDownloadUrl(e) {
    if (!this.id)
      throw new Error("Session ID not available - ensure session is ready before calling getFileDownloadUrl");
    const s = window.__hydra_copilot__;
    if (!s?.getFileDownloadUrl)
      throw new Error("File download not configured. Please provide getFileDownloadUrl in setCopilotConfig.");
    return (await s.getFileDownloadUrl({
      id: this.id,
      filename: e
    })).url;
  }
  /**
   * Add files to the upload queue
   */
  addUploadFiles(e) {
    const s = this.__uploadFiles.get();
    this.__uploadFiles.set([...s, ...e]);
  }
  /**
   * Update a specific upload file
   */
  updateUploadFile(e, s) {
    const n = this.__uploadFiles.get().map((o) => o.id === e ? { ...o, ...s } : o);
    this.__uploadFiles.set(n);
  }
  /**
   * Remove upload file
   */
  removeUploadFile(e) {
    const i = this.__uploadFiles.get().filter((n) => n.id !== e);
    this.__uploadFiles.set(i);
  }
  /**
   * Clear all upload files
   */
  clearUploadFiles() {
    this.__uploadFiles.set([]);
  }
  /**
   * Get all upload files that are pending
   */
  getPendingUploadFiles() {
    return this.__uploadFiles.get().filter((e) => e.status === "pending");
  }
}
function A(r) {
  return typeof r == "string" ? r : typeof r == "number" || typeof r == "boolean" ? String(r) : Array.isArray(r) || typeof r == "object" ? JSON.stringify(r) : String(r);
}
function N(r) {
  const e = /* @__PURE__ */ new Map(), s = /* @__PURE__ */ new Set(), i = r.filter((n) => n.was_approved === !0 && n.response != null);
  for (const n of i) {
    const o = r.findIndex(
      (a, h) => a !== n && !s.has(h) && a.name === n.name && a.needs_approval === !0 && JSON.stringify(a.args) === JSON.stringify(n.args)
    );
    o !== -1 && (e.set(o, n.response), s.add(o));
  }
  return e;
}
function $(r, e, s, i) {
  const n = [];
  let o = [], a = [], h = [];
  const p = {}, g = r.filter(
    (t) => {
      if (t.role === "user" && t.type === "tool_response") {
        for (const u of t.tool_responses)
          u.tool_call_id != null && (p[u.tool_call_id] == null && (p[u.tool_call_id] = []), p[u.tool_call_id].push(u));
        return !1;
      }
      return t.type === "text" || t.type === "content" || t.type === "tool_call" || t.type === "meta" || t.type === "error" || t.type === "compaction_start" || t.type === "compaction_complete";
    }
  );
  function m() {
    const t = o.filter((_) => _.type === "text"), u = o.find((_) => _.type === "meta" && "meta" in _);
    if (o = [], t.length === 0)
      return;
    const d = t.map((_) => _.text).join(""), l = u?.meta ?? {}, c = t[0]?.thought;
    let S;
    if (c === !0) {
      const _ = n[n.length - 1];
      _ != null && _.timestamp != null ? S = Math.max(0, t[t.length - 1].timestamp - _.timestamp) : S = 0;
    }
    n.push({
      role: "model",
      type: "text",
      text: d,
      timestamp: t[0].timestamp,
      meta: l,
      ...c === !0 && { thought: c },
      ...S != null && { duration: S }
    }), o = [];
  }
  function y() {
    if (a.length === 0)
      return;
    const t = a.find((l) => l.type === "text"), u = a.filter((l) => l.type === "content"), d = a[0];
    n.push({
      role: "user",
      type: "text",
      text: t?.text ?? "",
      attachments: u.map((l) => ({
        content: l.content,
        mime_type: l.mime_type,
        display_name: l.display_name
      })),
      timestamp: d.timestamp ?? 0
    }), a = [];
  }
  function I(t) {
    const u = p[t.id], d = e[t.name], l = t.needs_approval === !0 || t.is_client_tool === !0 && U(d?.needsApproval, t.args), c = l ? { ...t, needs_approval: !0 } : t;
    if (u == null || u.length === 0) {
      const f = t.is_client_tool === !0 && d?.humanInTheLoop === !0;
      return s.has(t.id) && !l && !f ? {
        ...c,
        status: "error",
        response: {
          result: null,
          error: k
        }
      } : {
        ...c,
        status: "executing",
        ...f && {
          respond: (P) => i(t.id, t.name, P)
        }
      };
    }
    const S = u.find((f) => f.response.error != null && f.response.error !== ""), _ = u.some((f) => f.finished), C = u.some((f) => f.was_approved === !0);
    if (S != null)
      return {
        ...c,
        status: "error",
        response: S.response,
        ...C && { was_approved: !0 }
      };
    const j = d?.type === "server" && d?.isStreaming === !0;
    return _ && u.length === 1 && !j ? {
      ...c,
      status: "success",
      response: u[0].response,
      ...C && { was_approved: !0 }
    } : {
      ...c,
      status: _ ? "success" : "executing",
      response: {
        result: u.map((f) => f.response.result),
        error: null
      },
      ...C && { was_approved: !0 }
    };
  }
  function T() {
    if (h.length === 0)
      return;
    const t = h.flatMap((l) => l.tool_calls.map(I)), u = N(t), d = t.map((l, c) => u.has(c) ? { ...l, response: u.get(c) } : l).filter((l) => l.was_approved !== !0);
    d.length > 0 && n.push({
      role: "model",
      type: "tool_call",
      tool_calls: d,
      timestamp: h[0].timestamp
    }), h = [];
  }
  for (const t of g)
    if (t.role === "model" && t.type === "text") {
      y(), T();
      const u = o.some((c) => c.type === "text"), d = t.thought === !0, l = o.some(
        (c) => c.type === "text" && "thought" in c && c.thought === !0
      );
      u && (d && !l || !d && l) && m(), o.push(t);
    } else if (t.role === "user" && (t.type === "text" || t.type === "content"))
      m(), T(), t.type === "text" && a.some((u) => u.type === "text") && y(), a.push(t);
    else if (t.role === "model" && t.type === "meta")
      y(), o.push(t), m();
    else if (t.role === "model" && t.type === "tool_call")
      m(), y(), h.push(t);
    else if (t.role === "model" && t.type === "compaction_start")
      m(), y(), T(), n.push({
        role: "model",
        type: "compaction",
        status: "compacting",
        timestamp: t.timestamp
      });
    else if (t.role === "model" && t.type === "compaction_complete") {
      m(), y(), T();
      const u = n.findIndex(
        (l) => l.role === "model" && l.type === "compaction" && l.status === "compacting"
      ), d = {
        role: "model",
        type: "compaction",
        status: "complete",
        summary: t.text,
        timestamp: t.timestamp
      };
      u >= 0 ? n[u] = d : n.push(d);
    } else
      t.role === "model" && t.type === "error" ? (m(), y(), T(), n.push(t)) : console.error("Unknown message type", t);
  m(), y(), T();
  for (let t = 0; t < n.length; t++) {
    const u = n[t];
    if (u.role !== "model" || u.type !== "tool_call" || !u.tool_calls.some((c) => c.status === "executing"))
      continue;
    n.slice(t + 1).some((c) => c.role === "model" && (c.type === "text" || c.type === "tool_call" || c.type === "error")) && (n[t] = {
      ...u,
      tool_calls: u.tool_calls.map((c) => c.status === "executing" ? { ...c, status: "success" } : c)
    });
  }
  return n;
}
function W(r) {
  return Object.values(r).filter((e) => e !== void 0 && e.type === "client").map((e) => ({
    name: e.name,
    description: typeof e.description == "string" ? x(e.description) : e.description,
    request_json_schema: e.requestSchema,
    json_response_schema: e.responseSchema
  }));
}
function G(r) {
  return Array.isArray(r) ? r.map((e) => x(e.instruction).trim()) : Object.values(r).filter((e) => e !== void 0).map((e) => x(e.instruction).trim());
}
function K(r) {
  return Array.isArray(r) ? r.map((e) => ({
    description: x(e.description),
    value: A(typeof e.value == "function" ? e.value() : e.value)
  })) : Object.values(r).filter((e) => e !== void 0).map((e) => ({
    description: x(e.description),
    value: A(typeof e.value == "function" ? e.value() : e.value)
  }));
}
function U(r, e) {
  if (typeof r == "function")
    try {
      return r(e);
    } catch {
      return !0;
    }
  return r === !0;
}
function x(r) {
  const e = r.split(`
`), s = e.filter((a) => a.trim().length > 0);
  if (s.length === 0)
    return r;
  const i = s.map((a) => {
    const h = a.match(/^(\s*)/);
    return h ? h[1].length : 0;
  }), n = Math.min(...i);
  return e.map((a) => a.trim().length === 0 ? a : a.slice(n)).join(`
`);
}
function Q(r, e) {
  for (const s of r)
    if (s.role === "model" && s.type === "tool_call") {
      const i = s.tool_calls.find((n) => n.id === e);
      if (i)
        return i;
    }
}
function O(r) {
  const e = r.get();
  if (e.length === 0)
    return [];
  r.set([]);
  const s = e.filter((o) => o.type === "text" && "text" in o), n = [...e.filter((o) => o.type !== "text")];
  if (s.length > 0) {
    const o = s.map((a) => "text" in a ? a.text : "").join(`

`);
    n.push({
      role: "user",
      type: "text",
      text: o,
      timestamp: v()
    });
  }
  return n;
}
function J(r) {
  return r instanceof DOMException && r.name === "AbortError" || r != null && typeof r == "object" && "name" in r && r.name === "CanceledError";
}
function B(r) {
  return {
    role: "user",
    type: "tool_response",
    tool_responses: r.map((e) => ({
      tool_call_id: e.id,
      name: e.name,
      response: { result: { status: "rejected" }, error: null },
      finished: !0
    })),
    timestamp: v()
  };
}
function R(r) {
  const e = {};
  for (const s of r)
    e[s.name] = {
      type: "server",
      name: s.name,
      description: s.description,
      requestSchema: s.request_json_schema,
      responseSchema: s.response_json_schema
    };
  return e;
}
export {
  X as CopilotSession,
  k as ORPHANED_TOOL_CALL_ERROR,
  Q as findToolCallById,
  K as prepareContextForTransport,
  G as prepareInstructionsForTransport,
  W as prepareToolsForTransport,
  U as resolveNeedsApproval,
  R as serverSideToolsToCopilotTools,
  A as stringifyForCopilot,
  x as undent,
  z as updatedStatus
};
